import {
  Box,
  InputLabel,
  Link,
  SxProps,
  TextField,
  Typography
} from '@mui/material';
import { useFormik } from 'formik';
import * as Yup from 'yup';
import React from 'react';
import { RadioGroup } from 'components/RadioGroup';
import { Button } from 'system/atoms';
import { useInviteUserToUserOrganizationMutation } from 'generated-graphql/hooks/apollo-hooks';
import { useToast, ToastType } from 'services/toast';
import { UserRole } from 'generated-graphql/types';
import { useRecoilValue } from 'recoil';
import { userAtom } from 'services/user/atoms';
import { useDrawer } from 'services/drawer/hooks';

const container: SxProps = {
  display: 'flex',
  flexDirection: 'column',
  gap: 3
};

const headerContainer: SxProps = {
  display: 'flex',
  flexDirection: 'column',
  gap: 1
};

const buttonsContainer: SxProps = {
  display: 'flex',
  flexDirection: 'row',
  gap: 1,
  mt: 2
};

type Props = {
  organizationId: string;
  refetch: () => void;
};

const roleOptions = [
  {
    value: UserRole.Admin,
    label: 'Admin'
  },
  {
    value: UserRole.Member,
    label: 'Member'
  }
];

// Matches backend error for duplicate org membership. Uses a regex to tolerate
// minor wording changes (e.g. "already in the organization" vs "already a member of the organization").
const USER_ALREADY_EXISTS_PATTERN = /already\s.+\borganization\b/i;

export const InviteUserDrawer: React.FC<Props> = ({
  organizationId,
  refetch
}) => {
  const user = useRecoilValue(userAtom);

  const { showToast } = useToast();
  const { closeDrawer } = useDrawer();

  const [inviteUserMutation, { loading }] =
    useInviteUserToUserOrganizationMutation();

  const { values, handleChange, handleBlur, touched, errors, handleSubmit } =
    useFormik({
      initialValues: {
        name: '',
        email: '',
        role: UserRole.Member
      },
      validationSchema: Yup.object({
        name: Yup.string().trim().required('Add a name'),
        email: Yup.string()
          .email('Invalid email address')
          .required('Add email address. Must include an @ symbol.'),
        role: Yup.string().required('Role required')
      }),
      onSubmit: async (values) => {
        try {
          await inviteUserMutation({
            variables: {
              input: {
                name: values.name.trim(),
                email: values.email,
                userOrganizationRole: values.role,
                organizationGroupId: user?.organizationGroupId || '',
                organizationId
              }
            }
          });
          closeDrawer();
          refetch();
          showToast({
            title: 'Sent',
            message: 'Invitation valid for seven days.',
            type: ToastType.Success
          });
        } catch (e: any) {
          const errorMessage = e?.message || '';
          const isUserAlreadyInOrg =
            USER_ALREADY_EXISTS_PATTERN.test(errorMessage);

          showToast({
            title: isUserAlreadyInOrg
              ? 'User already a member'
              : 'Something went wrong',
            message: isUserAlreadyInOrg ? (
              <Typography variant="body2" mt={1}>
                This user is already a member of this organization. They can log
                in directly with their existing account.
              </Typography>
            ) : (
              <Typography variant="body2" mt={1}>
                Invitation could not be sent. Try again or{' '}
                <Link href="mailto:support@joinpopp.com?subject=Failed to invite a user">
                  contact us
                </Link>
              </Typography>
            ),
            type: isUserAlreadyInOrg ? ToastType.Warning : ToastType.Error
          });
        }
      }
    });

  return (
    <Box minWidth="35vw" sx={container}>
      <Box sx={headerContainer}>
        <Typography fontSize="30px" fontWeight="400">
          Send invitation
        </Typography>
        <Typography variant="body2">
          This invitation valid for seven days.
        </Typography>
      </Box>

      <form onSubmit={handleSubmit}>
        <Box sx={{ mb: 3 }}>
          <InputLabel>Name</InputLabel>
          <TextField
            name="name"
            value={values.name}
            onChange={handleChange}
            onBlur={handleBlur}
            error={touched.name && Boolean(errors.name)}
            helperText={touched.name && errors.name}
            fullWidth
          />
        </Box>

        <Box sx={{ mb: 3 }}>
          <InputLabel>Email</InputLabel>
          <TextField
            name="email"
            type="email"
            value={values.email}
            onChange={handleChange}
            onBlur={handleBlur}
            error={touched.email && Boolean(errors.email)}
            helperText={touched.email && errors.email}
            fullWidth
          />
        </Box>

        <RadioGroup
          name="role"
          label="Role"
          radioTypographyPropsOverride={{ variant: 'body2' }}
          labelContainer={{ mb: 1 }}
          options={roleOptions}
          value={values.role}
          onChange={handleChange}
        />

        <Box sx={buttonsContainer}>
          <Button
            type="submit"
            variant="primary"
            loading={loading}
            disabled={loading}
          >
            Send
          </Button>
          <Button variant="tertiary" onClick={closeDrawer}>
            Cancel
          </Button>
        </Box>
      </form>
    </Box>
  );
};
